import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, FileText, Presentation, Search, X } from "lucide-react";
import { presentationApi } from "../../services/presentationApi";

const day = (value) => (value ? new Date(value).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "");
const SCOPE_NOUNS = { project: "Projet", client: "Client", programme: "Programme" };

// Nouvelle présentation : un titre et un rattachement (projet, client ou programme). `scope` fixé quand on part
// d'une fiche projet.
export const NewPresentationDialog = ({ scope: fixedScope, onClose, api = presentationApi, path = "/tools/presentations", backLabel }) => {
  const report = api.doc === "report";
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [query, setQuery] = useState("");
  const [found, setFound] = useState({ projects: [], clients: [], programmes: [] });
  const [scope, setScope] = useState(fixedScope || null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (fixedScope) return undefined;
    const timer = setTimeout(() => {
      api.scopes(query).then(setFound).catch((err) => setError(err.message));
    }, 200);
    return () => clearTimeout(timer);
  }, [query, fixedScope, api]);

  const create = async (event) => {
    event.preventDefault();
    if (!scope || !title.trim()) return;
    setBusy(true);
    setError("");
    try {
      const created = await api.create({ scope_type: scope.type, scope_id: scope.id, title: title.trim() });
      navigate(`${path}/${created.presentation.id}`, { state: { backLabel } });
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const option = (type, item, label, detail) => (
    <button key={`${type}-${item.id}`} type="button" onClick={() => setScope({ type, id: item.id, label })}
      className={`flex w-full items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-left text-sm ${
        scope?.type === type && scope?.id === item.id ? "bg-blue-50 text-blue-900" : "text-slate-700 hover:bg-slate-50"}`}>
      <span className="truncate">{label}</span>
      <span className="shrink-0 text-xs text-slate-400">{detail}</span>
    </button>
  );

  return (
    <div role="dialog" aria-modal="true" aria-label={report ? "Nouveau rapport" : "Nouvelle présentation"}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose}>
      <form onSubmit={create} onClick={(event) => event.stopPropagation()}
        className="w-full max-w-lg space-y-4 rounded-xl bg-white p-5 shadow-xl">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900">{report ? "Nouveau rapport" : "Nouvelle présentation"}</h2>
          <button type="button" onClick={onClose} aria-label="Fermer" className="rounded p-1 text-slate-400 hover:bg-slate-100"><X className="h-4 w-4" /></button>
        </div>
        <label className="block text-sm font-medium text-slate-700">
          Titre (couverture)
          <input autoFocus value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)}
            placeholder={report ? "Essais de répétabilité, étude de faisabilité…" : "Point d'avancement, restitution de l'étude…"}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none" />
        </label>
        {fixedScope ? (
          <p className="text-sm text-slate-600">{SCOPE_NOUNS[fixedScope.type]} : {fixedScope.label}</p>
        ) : (
          <div className="space-y-2">
            <span className="text-sm font-medium text-slate-700">Pour quel projet, client ou programme ?</span>
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
              <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Chercher"
                className="w-full rounded-lg border border-slate-300 py-2 pl-8 pr-3 text-sm focus:border-blue-400 focus:outline-none" />
            </div>
            <div className="max-h-64 space-y-2 overflow-y-auto">
              {found.projects.length > 0 && (
                <div>
                  <div className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Projets</div>
                  {found.projects.map((item) => option("project", item,
                    `${item.name}${item.project_number ? ` (${item.project_number})` : ""}`, item.client_name || ""))}
                </div>
              )}
              {found.clients.length > 0 && (
                <div>
                  <div className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Clients</div>
                  {found.clients.map((item) => option("client", item, item.name, report ? "tous ses rapports" : "toutes ses présentations"))}
                </div>
              )}
              {found.programmes.length > 0 && (
                <div>
                  <div className="px-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Programmes</div>
                  {found.programmes.map((item) => option("programme", item, item.name, "programme"))}
                </div>
              )}
            </div>
          </div>
        )}
        {error && <p className="text-sm text-rose-600">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full px-4 py-2 text-sm text-slate-600 hover:bg-slate-100">Annuler</button>
          <button type="submit" disabled={busy || !scope || !title.trim()}
            className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
            {busy ? "Création…" : "Créer"}
          </button>
        </div>
      </form>
    </div>
  );
};

// Présentations d'un rattachement (ou toutes), les plus récentes d'abord, avec l'état du PDF sur le Drive.
// backLabel : libellé de la flèche de retour de l'éditeur, qui ramène ici.
const PresentationsList = ({ params, emptyText = "Aucune présentation.", api = presentationApi, path = "/tools/presentations", backLabel }) => {
  const Icon = api.doc === "report" ? FileText : Presentation;
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const key = JSON.stringify(params || {});
  const load = useCallback(() => {
    api.list(JSON.parse(key)).then((data) => setItems(data.items || [])).catch((err) => setError(err.message));
  }, [key, api]);
  useEffect(() => { load(); }, [load]);

  if (error) return <p className="text-sm text-rose-600">{error}</p>;
  if (!items) return <p className="text-sm text-slate-500">Chargement…</p>;
  if (!items.length) return <p className="text-sm text-slate-500">{emptyText}</p>;
  return (
    <ul className="divide-y divide-slate-100">
      {items.map((item) => (
        <li key={item.id}>
          <button type="button" onClick={() => navigate(`${path}/${item.id}`, { state: { backLabel } })}
            className="flex w-full items-center gap-3 px-1 py-2.5 text-left hover:bg-slate-50">
            <Icon className="h-4 w-4 shrink-0 text-blue-600" />
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium text-slate-900">{item.title}</span>
              <span className="block truncate text-xs text-slate-500">
                {SCOPE_NOUNS[item.scope_type]} · {item.scope_label}
                {item.last_author ? ` · ${item.last_author}` : ""} · {day(item.updated_at)}
                {item.archived ? " · archivée" : ""}
              </span>
            </span>
            {item.drive_url && (
              <a href={item.drive_url} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}
                className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 hover:underline"
                title="PDF sur le Drive">
                <CheckCircle2 className="h-3 w-3" /> Drive
              </a>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
};

export default PresentationsList;
